import React, { useMemo, useState, useRef } from 'react';
import './ConsultaAvulsaPage.css';

export default function ConsultaAvulsaPage({ onCreateAccount }) {
  const [cpf, setCpf] = useState('');
  const [objetivo, setObjetivo] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [etapa, setEtapa] = useState('form'); // 'form' | 'resultado' | 'checkout'
  const [showQrModal, setShowQrModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const pixInputRef = useRef(null);
  const API_URL = useMemo(() => process.env.REACT_APP_API_URL || '', []);

  const objetivos = [
    { value: 'legitimo', label: 'Legítimo Interesse', desc: 'Atender interesses legítimos respeitando direitos fundamentais' },
    { value: 'identidade', label: 'Confirmação de Identidade', desc: 'Prevenção de Fraudes e autenticidade de identidade' },
    { value: 'credito', label: 'Ciclo de Crédito', desc: 'Análise Financeira, cobrança e concessão de crédito' },
    { value: 'contrato', label: 'Execução de Contrato', desc: 'Procedimentos preliminares relacionados a contratos' },
  ];

  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const handleCpfChange = (e) => {
    setCpf(formatCPF(e.target.value));
    if (erro) setErro(null);
  };

  // Função auxiliar para validar o cálculo do CPF
  function validarCPF(cpf) {
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

    let soma = 0;
    let resto;

    // Validação do 1º dígito verificador
    for (let i = 1; i <= 9; i++) {
      soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(9, 10))) return false;

    // Validação do 2º dígito verificador
    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(10, 11))) return false;

    return true;
  }

  async function pesquisar(e) {
    e.preventDefault();
    const cpfLimpo = cpf.replace(/\D/g, '');

    // Validação do CPF (tamanho + dígitos verificadores)
    if (!validarCPF(cpfLimpo)) {
      setErro('Informe um CPF válido.');
      return;
    }

    if (!objetivo) {
      setErro('Selecione o objetivo da consulta.');
      return;
    }

    setLoading(true);
    setErro(null);
    setResultado(null);

    try {
      const res = await fetch(`${API_URL}/api/consulta-avulsa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parametro: cpfLimpo, objetivo }),
      });
      const data = await res.json();
      if (data.sucesso) {
        setResultado(data.resultado);
        setEtapa('resultado');
      } else {
        setErro(data.erro || 'Não foi possível encontrar resultados para este CPF.');
      }
    } catch (err) {
      setErro('Erro ao conectar com o servidor. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }

  function maskCPF(value) {
    if (!value) return '';
    const clean = value.replace(/\D/g, '');
    if (clean.length === 11) {
      return `${clean.slice(0, 3)}.***.***-${clean.slice(9)}`;
    }
    // Se for texto misto (ex: "Nome — CPF"), mascara só a parte do CPF
    return value.replace(/\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11}/g, (match) => {
      const digits = match.replace(/\D/g, '');
      if (digits.length === 11) return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
      return match;
    });
  }

  function maskCNPJ(value) {
    if (!value) return '';
    const clean = value.replace(/\D/g, '');
    if (clean.length === 14) {
      return `${clean.slice(0, 2)}.***.***/${clean.slice(8, 12)}-${clean.slice(12)}`;
    }
    return value.replace(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}|\d{14}/g, (match) => {
      const digits = match.replace(/\D/g, '');
      if (digits.length === 14) return `${digits.slice(0, 2)}.***.***/${digits.slice(8, 12)}-${digits.slice(12)}`;
      return match;
    });
  }

  function maskNome(value) {
    if (!value) return '';
    return value.split(' ').map((part, i, arr) => {
      if (i === 0 || i === arr.length - 1) return part;
      return part[0] + '*'.repeat(Math.max(0, part.length - 1));
    }).join(' ');
  }

  function maskTelefone(value) {
    if (!value) return '';
    const clean = value.replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 4)}****-${clean.slice(8)}`;
    }
    return value.replace(/\(\d{2}\)\s\d{5}-\d{4}|\(\d{2}\)\s\d{4}-\d{4}|\d{11}/g, (match) => {
      const digits = match.replace(/\D/g, '');
      if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 4)}****-${digits.slice(8)}`;
      return match;
    });
  }

  function maskData(value) {
    if (!value) return '';
    const parts = value.split('/');
    if (parts.length === 3) {
      return `${parts[0]}/**/${parts[2]}`;
    }
    return value.replace(/\d{2}\/\d{2}\/\d{4}/g, (match) => {
      const p = match.split('/');
      return `${p[0]}/**/${p[2]}`;
    });
  }

  function maskEndereco(value) {
    if (!value) return '';
    return value.replace(/[a-zA-Zà-úÀ-Ú0-9]+/g, (match) => {
      if (match.length <= 3) return match;
      return match.slice(0, 3) + '*'.repeat(Math.max(0, match.length - 3));
    });
  }

  // Determina se o item é um vínculo (irmão, sócio, trabalho, parente, etc.)
  function isVinculo(label) {
    const vinculos = ['irmão', 'irmao', 'irma', 'irmã', 'socio', 'sócio', 'trabalho', 'emprego', 'empresa', 'vinculo', 'parente', 'conjuge', 'cônjuge', 'filho', 'filha'];
    return vinculos.some(v => label.toLowerCase().includes(v));
  }

  const handleCheckout = (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErro('Informe um e-mail válido para receber o relatório.');
      return;
    }
    setErro(null);
    const cpfLimpo = cpf.replace(/\D/g, '');
    setLoading(true);
    fetch(`${API_URL}/api/payments/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parametro: cpfLimpo, objetivo, email })
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && data && data.sucesso && data.data) {
          setPaymentData(data.data);
          setShowQrModal(true);
        } else {
          setErro(data.erro || 'Falha ao iniciar o pagamento PIX.');
        }
      })
      .catch(() => {
        setErro('Erro ao conectar ao servidor de pagamentos.');
      })
      .finally(() => setLoading(false));
  };

  const handleCopyPix = () => {
    if (paymentData?.pixQrCode) {
      navigator.clipboard.writeText(paymentData.pixQrCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handleCloseModal = () => {
    setShowQrModal(false);
    setPaymentData(null);
    setCopied(false);
  };

  return (
    <div className="consulta-root">
      {/* Header */}
      <header className="consulta-header">
        <div className="container">
          <div className="consulta-header-inner">
            <a href="/" className="consulta-logo">Análise Central</a>
            <nav className="consulta-header-nav">
              <a href="/">Página Inicial</a>
              <a href="#" className="active">Consultar CPF</a>
              <a href="/register">Criar Conta</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="consulta-hero">
        <div className="container">
          <span className="consulta-hero-label">Consulta Avulsa</span>
          <h1>Consultar CPF</h1>
          <p className="consulta-hero-desc">
            Consulta segura e rápida de CPF com dados completos e atualizados
          </p>
        </div>
      </div>

      <div className="consulta-layout container">
        {/* Formulário de Consulta */}
        {etapa === 'form' && (
          <div className="consulta-form-wrapper">
            <div className="form-card">
              <div className="form-card-header">
                <div className="form-icon">🔍</div>
                <h2>Informe os dados para consulta</h2>
                <p>Digite o CPF e selecione o objetivo da pesquisa</p>
              </div>

              <form onSubmit={pesquisar} className="consulta-form" noValidate>
                <div className="form-group">
                  <label htmlFor="cpf">
                    CPF <span className="required">*</span>
                  </label>
                  <input
                    id="cpf"
                    type="text"
                    placeholder="000.000.000-00"
                    value={cpf}
                    onChange={handleCpfChange}
                    maxLength={14}
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="objetivo">
                    Objetivo da consulta <span className="required">*</span>
                  </label>
                  <div className="select-wrapper">
                    <select
                      id="objetivo"
                      value={objetivo}
                      onChange={(e) => { setObjetivo(e.target.value); if (erro) setErro(null); }}
                    >
                      <option value="">Selecione o objetivo da consulta</option>
                      {objetivos.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {objetivo && (
                    <p className="objetivo-desc">
                      {objetivos.find(o => o.value === objetivo)?.desc}
                    </p>
                  )}
                </div>

                {erro && (
                  <div className="erro-box">
                    <span>⚠️</span>
                    <span>{erro}</span>
                  </div>
                )}

                <button type="submit" className="btn-primary btn-full" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Consultando...
                    </>
                  ) : (
                    'Pesquisar'
                  )}
                </button>
              </form>
            </div>

            <div className="info-sidebar">
              <div className="info-card-consulta">
                <div className="info-icon-lg">🛡️</div>
                <h3>Consulta Segura</h3>
                <p>Todos os dados são tratados em conformidade com a LGPD e criptografados durante a transmissão.</p>
              </div>
              <div className="info-card-consulta">
                <div className="info-icon-lg">⚡</div>
                <h3>Resultado Instantâneo</h3>
                <p>Receba o relatório parcial em segundos. O relatório completo é enviado por e-mail após a confirmação do pagamento.</p>
              </div>
              <div className="info-card-consulta">
                <div className="info-icon-lg">💰</div>
                <h3>Preço Justo</h3>
                <p>De <span className="preco-riscado">R$ 25,90</span> por <span className="preco-destaque">R$ 16,90</span>. Economize R$ 9,00 com desconto de 35%.</p>
              </div>
            </div>
          </div>
        )}

        {/* Resultado Parcial */}
        {etapa === 'resultado' && resultado && (
          <div className="resultado-wrapper">
            <div className="resultado-header">
              <button className="btn-voltar" onClick={() => { setEtapa('form'); setResultado(null); setErro(null); }}>
                ← Nova consulta
              </button>
              <span className="resultado-badge">Visualização Limitada</span>
            </div>

            <div className="resultado-grid">
              {/* Relatório Parcial — Renderização Dinâmica */}
              <div className="resultado-card parcial">
                <div className="resultado-card-header">
                  <span className="resultado-icon">👁️</span>
                  <div>
                    <h3>Relatório Parcial</h3>
                    <p>Visualização Limitada — Dados mascarados</p>
                  </div>
                </div>

                <div className="dados-lista">
                  {resultado.dados && resultado.dados.length > 0 ? (
                    resultado.dados.map((item, index) => {
                      const valorMascarado = item.value;
                      const vinculo = isVinculo(item.label);

                      return (
                        <div key={index} className={`dado-item ${vinculo ? 'vinculo' : ''}`}>
                          <span className="dado-label">{item.label}</span>
                          <span className="dado-valor">
                            {valorMascarado}
                            {item.tag && <span className="vinculo-tag">{item.tag}</span>}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dado-item">
                      <span className="dado-valor">Nenhum dado encontrado.</span>
                    </div>
                  )}
                </div>

                <div className="mascara-aviso">
                  <span>🔒</span>
                  <p>Os dados estão mascarados para proteção. Adquira o relatório completo para visualizar todas as informações.</p>
                </div>
              </div>

              {/* Card de Compra */}
              <div className="checkout-card">
                <div className="checkout-header">
                  <span className="checkout-tag">🎯 Acesso detalhado Agora!</span>
                  <h3>Consulta CPF Completa</h3>
                  <p className="checkout-cpf">{maskCPF(cpf)}</p>
                </div>

                <div className="checkout-preco">
                  <div className="preco-antigo">
                    <span>De</span>
                    <span className="valor-riscado">R$ 25,90</span>
                  </div>
                  <div className="preco-atual">
                    <span className="por">por</span>
                    <span className="valor">R$ 16,90</span>
                  </div>
                </div>

                <div className="checkout-economia">
                  <span className="economia-badge">
                    💰 Você economiza R$ 9,00 — Desconto de 35% aplicado automaticamente!
                  </span>
                </div>

                <form onSubmit={handleCheckout} className="checkout-form" noValidate>
                  <div className="form-group">
                    <label htmlFor="email-checkout">
                      Seu E-mail para receber o relatório
                    </label>
                    <input
                      id="email-checkout"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (erro) setErro(null); }}
                      autoComplete="email"
                    />
                  </div>

                  {erro && (
                    <div className="erro-box">
                      <span>⚠️</span>
                      <span>{erro}</span>
                    </div>
                  )}

                  <button type="submit" className="btn-primary btn-full btn-pix" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Gerando QR Code...
                      </>
                    ) : (
                      <>
                        <span className="pix-icon">⚡</span>
                        Pagar com PIX
                      </>
                    )}
                  </button>
                </form>

                <div className="checkout-garantias">
                  <div className="garantia-item">
                    <span>✓</span>
                    <span>Pagamento seguro via PIX</span>
                  </div>
                  <div className="garantia-item">
                    <span>✓</span>
                    <span>Relatório enviado em até 5 minutos</span>
                  </div>
                  <div className="garantia-item">
                    <span>✓</span>
                    <span>Acesso ao histórico por 30 dias</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal do QR Code PIX */}
      {showQrModal && paymentData && (
        <div className="qr-modal-overlay" onClick={handleCloseModal}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={handleCloseModal} title="Fechar">
              ✕
            </button>

            <div className="qr-modal-header">
              <div className="qr-modal-icon">⚡</div>
              <h2>Pagamento via PIX</h2>
              <p>Escaneie o QR Code ou copie o código para realizar o pagamento</p>
            </div>

            <div className="qr-modal-body">
              {/* QR Code */}
              <div className="qr-code-wrapper">
                {paymentData.qrCodeImage ? (
                  <img 
                    src={paymentData.qrCodeImage} 
                    alt="QR Code PIX" 
                    className="qr-code-image"
                  />
                ) : (
                  <div className="qr-code-placeholder">
                    <span>📱</span>
                    <p>QR Code não disponível</p>
                  </div>
                )}
              </div>

              {/* Código PIX Copia e Cola */}
              <div className="pix-code-section">
                <label>Código PIX (Copia e Cola)</label>
                <div className="pix-code-box">
                  <input
                    ref={pixInputRef}
                    type="text"
                    value={paymentData.pixQrCode || ''}
                    readOnly
                  />
                  <button 
                    className={`btn-copy-pix ${copied ? 'copied' : ''}`}
                    onClick={handleCopyPix}
                  >
                    {copied ? '✓ Copiado!' : '📋 Copiar'}
                  </button>
                </div>
              </div>

              {/* Informações do pagamento */}
              <div className="qr-payment-info">
                <div className="qr-info-row">
                  <span className="qr-info-label">Valor:</span>
                  <span className="qr-info-value">R$ {paymentData.amount || '16,90'}</span>
                </div>
                <div className="qr-info-row">
                  <span className="qr-info-label">Descrição:</span>
                  <span className="qr-info-value">{paymentData.description || 'Consulta unitária'}</span>
                </div>
                {/* <div className="qr-info-row">
                  <span className="qr-info-label">Status:</span>
                  <span className="qr-info-value qr-status-pending">
                    ⏳ Aguardando pagamento
                  </span>
                </div> */}
              </div>

              {/* Aviso importante */}
              <div className="qr-aviso-importante">
                <div className="qr-aviso-icon">📧</div>
                <div className="qr-aviso-content">
                  <h4>Relatório enviado automaticamente</h4>
                  <p>
                    Após efetuar o pagamento, o <strong>PDF completo do resultado da consulta</strong> será enviado automaticamente para o e-mail: <strong>{email}</strong>
                  </p>
                  <p className="qr-aviso-sub">
                    O envio ocorre em até 5 minutos após a confirmação do pagamento.
                  </p>
                </div>
              </div>

              {/* Instruções */}
              <div className="qr-instrucoes">
                <h4>Como pagar:</h4>
                <ol>
                  <li>Abra o app do seu banco</li>
                  <li>Escolha a opção <strong>PIX → Pagar com QR Code</strong></li>
                  <li>Escaneie o QR Code acima ou cole o código PIX copiado</li>
                  <li>Confirme o pagamento no app</li>
                  <li>Aguarde a confirmação — o relatório será enviado para seu e-mail</li>
                </ol>
              </div>
            </div>

            <div className="qr-modal-footer">
              <button className="btn-fechar-modal" onClick={handleCloseModal}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="consulta-footer">
        <div className="container">
          <div className="consulta-footer-inner">
            <p>© Análise Central. Todos os direitos reservados.</p>
            <div className="consulta-footer-links">
              <a href="/">Página Inicial</a>
              <a href="/lgpd/terms">Termos de Uso</a>
              <a href="/lgpd/privacy">Política de Privacidade</a>
              <a href="/lgpd/exclusion">LGPD</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}